import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import {
    appendCassetteBatchItems,
    deleteCassetteBatch,
    downloadCassetteBatchExport,
    downloadFileByUrl,
    loadCassetteBatchDetail,
    loadCassetteReplacementModules,
    submitCassetteBatchSend,
    submitCassetteModuleAction,
} from './api';
import { cassetteFallbackDetailMap } from './cassetteFallbacks';
import SearchableSelect from './SearchableSelect';


const initialActionState = {
    module: null,
    mode: 'vandal',
    comment: '',
    dateRepair: '',
    replacementModuleId: '',
    resultModule: null,
    replacementModule: null,
    loading: false,
    error: '',
    success: '',
};

const initialAppendState = {
    serialNumbers: '',
    file: null,
    result: null,
    loading: false,
    error: '',
};


function buildBatchKey(territoryId, arrivalDate, numberKits) {
    return `${territoryId}:${arrivalDate}:${numberKits}`;
}


function formatDate(value) {
    if (!value) {
        return '—';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return value;
    }

    return date.toLocaleDateString('ru-RU');
}


function extractErrorMessage(error, fallback) {
    return error?.response?.data?.detail || fallback;
}


function Pagination({ pagination, onPageChange }) {
    if (!pagination || pagination.total_pages <= 1) {
        return null;
    }

    return (
        <div className="shell-paginationRow">
            <button
                type="button"
                className="shell-actionButton shell-actionButton--secondary shell-actionButton--small"
                onClick={() => onPageChange(pagination.page - 1)}
                disabled={!pagination.has_previous}
            >
                Назад
            </button>
            <span className="shell-paginationMeta">
                Страница {pagination.page} из {pagination.total_pages}
            </span>
            <button
                type="button"
                className="shell-actionButton shell-actionButton--secondary shell-actionButton--small"
                onClick={() => onPageChange(pagination.page + 1)}
                disabled={!pagination.has_next}
            >
                Далее
            </button>
        </div>
    );
}


export default function CassetteBatchDetailPage() {
    const { territoryId, arrivalDate, numberKits } = useParams();
    const navigate = useNavigate();
    const [detailState, setDetailState] = useState({
        data: null,
        loading: true,
        warning: '',
    });
    const [detailSearch, setDetailSearch] = useState('');
    const [appliedDetailSearch, setAppliedDetailSearch] = useState('');
    const [detailPage, setDetailPage] = useState(1);
    const [reloadKey, setReloadKey] = useState(0);
    const [batchSendFile, setBatchSendFile] = useState(null);
    const [batchSendFileKey, setBatchSendFileKey] = useState(0);
    const [appendFileKey, setAppendFileKey] = useState(0);
    const [batchSendState, setBatchSendState] = useState({
        loading: false,
        error: '',
        success: '',
        request: null,
    });
    const [appendState, setAppendState] = useState(initialAppendState);
    const [deleteState, setDeleteState] = useState({
        loading: false,
        error: '',
    });
    const [exportLoading, setExportLoading] = useState(false);
    const [actionState, setActionState] = useState(initialActionState);
    const [replacementState, setReplacementState] = useState({
        items: [],
        loading: false,
        loaded: false,
        error: '',
    });
    const [toast, setToast] = useState(null);

    useEffect(() => {
        const timer = window.setTimeout(() => {
            const nextSearch = detailSearch.trim();
            setAppliedDetailSearch((current) => {
                if (current === nextSearch) {
                    return current;
                }
                setDetailPage(1);
                return nextSearch;
            });
        }, 300);

        return () => window.clearTimeout(timer);
    }, [detailSearch]);

    useEffect(() => {
        let cancelled = false;

        async function loadDetail() {
            setDetailState((current) => ({
                ...current,
                loading: true,
            }));

            try {
                const payload = await loadCassetteBatchDetail(territoryId, arrivalDate, numberKits, {
                    search: appliedDetailSearch,
                    page: detailPage,
                    page_size: 25,
                });
                if (!cancelled) {
                    setDetailState({
                        data: payload,
                        loading: false,
                        warning: '',
                    });
                }
            } catch (_error) {
                if (!cancelled) {
                    setDetailState({
                        data: cassetteFallbackDetailMap[buildBatchKey(territoryId, arrivalDate, numberKits)] || null,
                        loading: false,
                        warning: 'Не удалось загрузить состав партии. Показан локальный пример.',
                    });
                }
            }
        }

        loadDetail();

        return () => {
            cancelled = true;
        };
    }, [appliedDetailSearch, arrivalDate, detailPage, numberKits, reloadKey, territoryId]);

    useEffect(() => {
        if (!toast) {
            return undefined;
        }
        const timer = window.setTimeout(() => setToast(null), 2600);
        return () => window.clearTimeout(timer);
    }, [toast]);

    useEffect(() => {
        if (actionState.mode !== 'guarantee' || !actionState.module || replacementState.loaded || replacementState.loading) {
            return;
        }

        let cancelled = false;

        async function loadReplacements() {
            setReplacementState({
                items: [],
                loading: true,
                loaded: false,
                error: '',
            });

            try {
                const payload = await loadCassetteReplacementModules();
                if (!cancelled) {
                    setReplacementState({
                        items: payload.items || [],
                        loading: false,
                        loaded: true,
                        error: '',
                    });
                }
            } catch (error) {
                if (!cancelled) {
                    setReplacementState({
                        items: [],
                        loading: false,
                        loaded: true,
                        error: extractErrorMessage(error, 'Не удалось загрузить подменные кассеты.'),
                    });
                }
            }
        }

        loadReplacements();

        return () => {
            cancelled = true;
        };
    }, [actionState.mode, actionState.module, replacementState.loaded, replacementState.loading]);

    function openAction(module, mode) {
        setActionState({
            module,
            mode,
            comment: module.comment || '',
            dateRepair: module.date_repair || '',
            replacementModuleId: '',
            resultModule: null,
            replacementModule: null,
            loading: false,
            error: '',
            success: '',
        });
        if (mode === 'guarantee') {
            setReplacementState({
                items: [],
                loading: false,
                loaded: false,
                error: '',
            });
        }
    }

    async function handleBatchSend(event) {
        event.preventDefault();

        setBatchSendState({
            loading: true,
            error: '',
            success: '',
            request: null,
        });

        const formData = new FormData();
        if (batchSendFile) {
            formData.append('logistics_file', batchSendFile);
        }

        try {
            const response = await submitCassetteBatchSend(territoryId, arrivalDate, numberKits, formData);
            setBatchSendState({
                loading: false,
                error: '',
                success: response.detail,
                request: response.request || null,
            });
            setBatchSendFile(null);
            setBatchSendFileKey((current) => current + 1);
            setToast({ type: 'success', text: response.detail });
        } catch (error) {
            const message = extractErrorMessage(error, 'Не удалось передать партию логисту.');
            setBatchSendState({
                loading: false,
                error: message,
                success: '',
                request: null,
            });
            setToast({ type: 'error', text: message });
        }
    }

    async function handleAppendSubmit(event) {
        event.preventDefault();
        setAppendState((current) => ({
            ...current,
            loading: true,
            error: '',
            result: null,
        }));

        const formData = new FormData();
        if (appendState.file) {
            formData.append('file', appendState.file);
        }
        if (appendState.serialNumbers.trim()) {
            formData.append('serial_numbers', appendState.serialNumbers);
        }

        try {
            const response = await appendCassetteBatchItems(territoryId, arrivalDate, numberKits, formData);
            setAppendState({
                serialNumbers: '',
                file: null,
                result: response,
                loading: false,
                error: '',
            });
            setAppendFileKey((current) => current + 1);
            setReloadKey((current) => current + 1);
            setToast({
                type: response.invalid_count ? 'error' : 'success',
                text: response.detail,
            });
        } catch (error) {
            const message = extractErrorMessage(error, 'Не удалось дозаполнить партию кассет.');
            setAppendState((current) => ({
                ...current,
                loading: false,
                error: message,
            }));
            setToast({ type: 'error', text: message });
        }
    }

    async function handleBatchDelete() {
        if (!window.confirm('Удалить всю партию кассет? Это действие необратимо.')) {
            return;
        }

        setDeleteState({
            loading: true,
            error: '',
        });

        try {
            const response = await deleteCassetteBatch(territoryId, arrivalDate, numberKits);
            setToast({ type: 'success', text: response.detail });
            navigate('/app/cassettes');
        } catch (error) {
            const message = extractErrorMessage(error, 'Не удалось удалить партию кассет.');
            setDeleteState({
                loading: false,
                error: message,
            });
            setToast({ type: 'error', text: message });
        }
    }

    async function handleActionSubmit(event) {
        event.preventDefault();
        if (!actionState.module) {
            return;
        }

        setActionState((current) => ({
            ...current,
            loading: true,
            error: '',
            success: '',
            resultModule: null,
            replacementModule: null,
        }));

        const formData = new FormData();
        formData.append('action', actionState.mode);
        if (actionState.mode === 'vandal') {
            formData.append('comment', actionState.comment);
            formData.append('date_repair', actionState.dateRepair);
        } else {
            formData.append('replacement_module_id', actionState.replacementModuleId);
        }

        try {
            const response = await submitCassetteModuleAction(actionState.module.serial_number, formData);
            setActionState((current) => ({
                ...current,
                loading: false,
                error: '',
                success: response.detail,
                resultModule: response.module || null,
                replacementModule: response.replacement_module || null,
            }));
            setToast({ type: 'success', text: response.detail });
        } catch (error) {
            const message = extractErrorMessage(error, 'Не удалось применить действие к кассете.');
            setActionState((current) => ({
                ...current,
                loading: false,
                error: message,
                success: '',
                resultModule: null,
                replacementModule: null,
            }));
            setToast({ type: 'error', text: message });
        }
    }

    async function handleRequestFileDownload(url, fallbackFilename) {
        try {
            await downloadFileByUrl(url, fallbackFilename);
        } catch (error) {
            const message = extractErrorMessage(error, 'Не удалось скачать файл.');
            setToast({ type: 'error', text: message });
        }
    }

    async function handleBatchExport() {
        setExportLoading(true);
        try {
            await downloadCassetteBatchExport(territoryId, arrivalDate, numberKits, {
                search: detailSearch.trim(),
            });
        } catch (error) {
            const message = extractErrorMessage(error, 'Не удалось скачать таблицу партии.');
            setToast({ type: 'error', text: message });
        } finally {
            setExportLoading(false);
        }
    }

    function clearDetailSearch() {
        setDetailSearch('');
    }

    const batch = detailState.data?.batch;
    const canMutate = detailState.data?.permissions?.can_mutate;
    const canDeleteBatch = detailState.data?.permissions?.can_delete_batch;
    const isInitialLoading = detailState.loading && !detailState.data;

    return (
        <div className="shell-page">
            {toast ? (
                <div className="shell-toastStack">
                    <div className={`shell-toast ${toast.type === 'error' ? 'shell-toast--error' : ''}`}>
                        {toast.text}
                    </div>
                </div>
            ) : null}

            <section className="shell-section shell-section--pageTop">
                <div className="shell-pageHeaderCompact">
                    <div className="shell-pageBandIntro">
                        <h3>Партия кассет</h3>
                    </div>
                    <div className="shell-actionCluster">
                        <button
                            type="button"
                            className="shell-actionButton shell-actionButton--secondary"
                            onClick={() => navigate('/app/cassettes')}
                        >
                            Назад к кассетам
                        </button>
                        {canDeleteBatch ? (
                            <button
                                type="button"
                                className="shell-actionButton shell-actionButton--danger"
                                onClick={handleBatchDelete}
                                disabled={deleteState.loading}
                            >
                                {deleteState.loading ? 'Удаляем...' : 'Удалить партию'}
                            </button>
                        ) : null}
                    </div>
                </div>
            </section>

            {detailState.warning ? <div className="shell-banner">{detailState.warning}</div> : null}

            {isInitialLoading ? (
                <section className="shell-section">
                    <div className="shell-emptyState shell-emptyState--compact">Загружаем состав партии...</div>
                </section>
            ) : batch ? (
                <>
                    <section className="shell-section">
                        <div className="shell-sectionHeader">
                            <div>
                                <h3>{batch.territory.name}</h3>
                            </div>
                            <span>КИЦ #{batch.number_kits}</span>
                        </div>

                        <div className="shell-pageSummary shell-pageSummary--queueTop">
                            <div className="shell-pageSummaryCard shell-pageSummaryCard--compact">
                                <span>Дата поступления</span>
                                <strong>{formatDate(batch.arrival_date)}</strong>
                            </div>
                            <div className="shell-pageSummaryCard shell-pageSummaryCard--compact">
                                <span>Всего кассет</span>
                                <strong>{batch.total_count}</strong>
                            </div>
                            <div className="shell-pageSummaryCard shell-pageSummaryCard--compact">
                                <span>GOOD / BAD</span>
                                <strong>{batch.good_count} / {batch.bad_count}</strong>
                            </div>
                            <div className="shell-pageSummaryCard shell-pageSummaryCard--compact">
                                <span>Остаток до SLA</span>
                                <strong>{batch.days_left}</strong>
                            </div>
                        </div>

                        {batch.has_pending_repair ? (
                            <div className="shell-banner shell-banner--warning">
                                В партии есть кассеты со статусом `Ожидает ремонт`. Передача логисту заблокирована.
                            </div>
                        ) : null}
                    </section>

                    <section className="shell-section">
                        <div className="shell-grid shell-grid--main">
                            {canMutate ? (
                                <div className="shell-section shell-section--embedded shell-section--batchSend">
                                    <div className="shell-sectionHeader shell-sectionHeader--nested">
                                        <div>
                                            <h3>Передача логисту</h3>
                                        </div>
                                    </div>

                                    <div className="shell-requestActionPanel shell-requestActionPanel--batchSend">
                                        <form className="shell-searchForm shell-searchForm--embedded" onSubmit={handleBatchSend}>
                                            <label className="shell-field shell-field--full">
                                                <span>Акт приёма-передачи</span>
                                                <input
                                                    key={batchSendFileKey}
                                                    type="file"
                                                    accept=".xls,.xlsx"
                                                    onChange={(event) => setBatchSendFile(event.target.files?.[0] || null)}
                                                />
                                            </label>

                                            <div className="shell-searchActions">
                                                <button
                                                    type="submit"
                                                    className="shell-actionButton shell-actionButton--primary"
                                                    disabled={batchSendState.loading || batch.has_pending_repair}
                                                >
                                                    {batchSendState.loading ? 'Передаем...' : 'Передать логисту'}
                                                </button>
                                            </div>
                                        </form>

                                        {batchSendState.error ? <div className="shell-inlineMessage shell-inlineMessage--error">{batchSendState.error}</div> : null}
                                        {batchSendState.request ? (
                                            <div className="shell-requestActionCard shell-requestActionCard--result">
                                                <span className="shell-cardLabel">Созданная заявка</span>
                                                <div className="shell-requestDetail shell-requestDetail--compact">
                                                    <div className="shell-requestDetailGrid">
                                                        <div className="shell-requestDetailItem">
                                                            <span>Заявка</span>
                                                            <strong>{batchSendState.request.application_name}</strong>
                                                        </div>
                                                        <div className="shell-requestDetailItem">
                                                            <span>Slug</span>
                                                            <strong>{batchSendState.request.slug}</strong>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="shell-requestActionButtons">
                                                    {batchSendState.request.excel_file_url ? (
                                                        <button
                                                            type="button"
                                                            className="shell-actionButton shell-actionButton--secondary shell-actionButton--small"
                                                            onClick={() => handleRequestFileDownload(batchSendState.request.excel_file_url, `${batchSendState.request.slug}-act.xlsx`)}
                                                        >
                                                            Скачать акт
                                                        </button>
                                                    ) : null}
                                                    {batchSendState.request.pdf_file_url ? (
                                                        <button
                                                            type="button"
                                                            className="shell-actionButton shell-actionButton--secondary shell-actionButton--small"
                                                            onClick={() => handleRequestFileDownload(batchSendState.request.pdf_file_url, `${batchSendState.request.slug}.pdf`)}
                                                        >
                                                            Скачать PDF
                                                        </button>
                                                    ) : null}
                                                </div>
                                            </div>
                                        ) : null}
                                    </div>
                                </div>
                            ) : null}

                            {canMutate ? (
                                <div className="shell-section shell-section--embedded">
                                    <div className="shell-sectionHeader shell-sectionHeader--nested">
                                        <div>
                                            <h3>Дозаполнить партию</h3>
                                        </div>
                                    </div>

                                    <form className="shell-searchForm shell-searchForm--embedded" onSubmit={handleAppendSubmit}>
                                        <label className="shell-field shell-field--full">
                                            <span>Серийные номера вручную</span>
                                            <textarea
                                                value={appendState.serialNumbers}
                                                onChange={(event) => setAppendState((current) => ({
                                                    ...current,
                                                    serialNumbers: event.target.value,
                                                }))}
                                                placeholder="Вставь серийники по одному в строке или через запятую"
                                            />
                                        </label>

                                        <label className="shell-field shell-field--full">
                                            <span>Excel с серийными номерами</span>
                                            <input
                                                key={appendFileKey}
                                                type="file"
                                                accept=".xls,.xlsx"
                                                onChange={(event) => setAppendState((current) => ({
                                                    ...current,
                                                    file: event.target.files?.[0] || null,
                                                }))}
                                            />
                                        </label>

                                        <div className="shell-searchActions">
                                            <button
                                                type="submit"
                                                className="shell-actionButton shell-actionButton--primary"
                                                disabled={appendState.loading}
                                            >
                                                {appendState.loading ? 'Добавляем...' : 'Добавить в партию'}
                                            </button>
                                        </div>
                                    </form>

                                    {appendState.error ? (
                                        <div className="shell-inlineMessage shell-inlineMessage--error">{appendState.error}</div>
                                    ) : null}

                                    {appendState.result ? (
                                        <div className="shell-requestActionCard">
                                            <span className="shell-cardLabel">Результат дозагрузки</span>
                                            <div className="shell-requestDetail shell-requestDetail--compact">
                                                <div className="shell-requestDetailGrid">
                                                    <div className="shell-requestDetailItem">
                                                        <span>Создано</span>
                                                        <strong>{appendState.result.created_count}</strong>
                                                    </div>
                                                    <div className="shell-requestDetailItem">
                                                        <span>Ошибок</span>
                                                        <strong>{appendState.result.invalid_count}</strong>
                                                    </div>
                                                </div>
                                            </div>
                                            {appendState.result.error_report_url ? (
                                                <div className="shell-requestActionButtons">
                                                    <button
                                                        type="button"
                                                        className="shell-actionButton shell-actionButton--secondary shell-actionButton--small"
                                                        onClick={() => handleRequestFileDownload(appendState.result.error_report_url, 'cassette-batch-errors.xlsx')}
                                                    >
                                                        Скачать ошибки
                                                    </button>
                                                </div>
                                            ) : null}
                                        </div>
                                    ) : null}
                                </div>
                            ) : null}
                        </div>
                    </section>

                    {deleteState.error ? <div className="shell-banner shell-banner--warning">{deleteState.error}</div> : null}

                    <section className="shell-section">
                        <div className="shell-sectionHeader">
                            <div>
                                <h3>Состав партии</h3>
                            </div>
                            <span>{detailState.data?.results?.pagination?.total_items ?? 0} шт.</span>
                        </div>

                        <div className="shell-toolbarRow">
                            <label className="shell-field shell-field--toolbar">
                                <span>Поиск по серийному номеру</span>
                                <input
                                    type="text"
                                    value={detailSearch}
                                    onChange={(event) => setDetailSearch(event.target.value)}
                                    placeholder="Например, CEGD200456"
                                />
                            </label>
                            {detailSearch ? (
                                <button
                                    type="button"
                                    className="shell-actionButton shell-actionButton--secondary"
                                    onClick={clearDetailSearch}
                                >
                                    Сбросить
                                </button>
                            ) : null}
                            <button
                                type="button"
                                className="shell-actionButton shell-actionButton--secondary"
                                onClick={handleBatchExport}
                                disabled={exportLoading}
                            >
                                {exportLoading ? 'Готовим XLSX...' : 'Скачать XLSX'}
                            </button>
                            {detailState.loading ? (
                                <span className="shell-toolbarMeta">Обновляем таблицу...</span>
                            ) : null}
                        </div>

                        <div className="shell-tableWrap">
                            <table className="shell-table">
                                <thead>
                                    <tr>
                                        <th>Серийный номер</th>
                                        <th>Партия / система</th>
                                        <th>Статус ремонта</th>
                                        <th>Статус перемещения</th>
                                        <th>Дата ремонта</th>
                                        <th>Инженер</th>
                                        <th>Комментарий</th>
                                        {canMutate ? <th>Действия</th> : null}
                                    </tr>
                                </thead>
                                <tbody>
                                    {(detailState.data?.results?.items || []).map((module) => (
                                        <tr key={module.id} className="shell-tableRow">
                                            <td><strong>{module.serial_number}</strong></td>
                                            <td>
                                                {module.party_number || '—'}
                                                <div className="shell-tableHint">{module.system || '—'}</div>
                                            </td>
                                            <td>{module.repair_status || '—'}</td>
                                            <td>{module.movement_status || '—'}</td>
                                            <td>{formatDate(module.date_repair)}</td>
                                            <td>{module.engineer || '—'}</td>
                                            <td>{module.comment || '—'}</td>
                                            {canMutate ? (
                                                <td>
                                                    <div className="shell-tableActions">
                                                        <button
                                                            type="button"
                                                            className="shell-actionButton shell-actionButton--secondary shell-actionButton--small"
                                                            onClick={() => openAction(module, 'vandal')}
                                                        >
                                                            Вандал
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="shell-actionButton shell-actionButton--primary shell-actionButton--small"
                                                            onClick={() => openAction(module, 'guarantee')}
                                                        >
                                                            Гарантия
                                                        </button>
                                                    </div>
                                                </td>
                                            ) : null}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {!detailState.loading && !(detailState.data?.results?.items || []).length ? (
                            <div className="shell-emptyState shell-emptyState--compact">В выбранной партии нет кассет по текущему фильтру.</div>
                        ) : null}

                        <Pagination
                            pagination={detailState.data?.results?.pagination}
                            onPageChange={setDetailPage}
                        />
                    </section>

                    {actionState.module ? (
                        <section className="shell-section">
                            <div className="shell-sectionHeader">
                                <div>
                                    <h3>{actionState.module.serial_number}</h3>
                                </div>
                            </div>

                            <div className="shell-tabRow">
                                <button
                                    type="button"
                                    className={`shell-tabButton${actionState.mode === 'vandal' ? ' is-active' : ''}`}
                                    onClick={() => setActionState((current) => ({
                                        ...current,
                                        mode: 'vandal',
                                        error: '',
                                        success: '',
                                        resultModule: null,
                                        replacementModule: null,
                                    }))}
                                >
                                    Вандал
                                </button>
                                <button
                                    type="button"
                                    className={`shell-tabButton${actionState.mode === 'guarantee' ? ' is-active' : ''}`}
                                    onClick={() => setActionState((current) => ({
                                        ...current,
                                        mode: 'guarantee',
                                        error: '',
                                        success: '',
                                        resultModule: null,
                                        replacementModule: null,
                                    }))}
                                >
                                    Гарантия
                                </button>
                            </div>

                            <form className="shell-searchForm shell-searchForm--embedded" onSubmit={handleActionSubmit}>
                                {actionState.mode === 'vandal' ? (
                                    <>
                                        <label className="shell-field shell-field--full">
                                            <span>Комментарий</span>
                                            <input
                                                type="text"
                                                value={actionState.comment}
                                                onChange={(event) => setActionState((current) => ({
                                                    ...current,
                                                    comment: event.target.value,
                                                }))}
                                                placeholder="Например, вандал / нет ремонта"
                                            />
                                        </label>
                                        <label className="shell-field">
                                            <span>Дата ремонта</span>
                                            <input
                                                type="date"
                                                value={actionState.dateRepair}
                                                onChange={(event) => setActionState((current) => ({
                                                    ...current,
                                                    dateRepair: event.target.value,
                                                }))}
                                            />
                                        </label>
                                    </>
                                ) : (
                                    <label className="shell-field shell-field--full">
                                        <span>Подменная кассета</span>
                                        <SearchableSelect
                                            value={actionState.replacementModuleId}
                                            onChange={(value) => setActionState((current) => ({
                                                ...current,
                                                replacementModuleId: value,
                                            }))}
                                            options={replacementState.items.map((item) => ({
                                                value: String(item.id),
                                                label: item.serial_number,
                                                meta: [item.party_number, item.system, item.location?.city].filter(Boolean).join(' • '),
                                            }))}
                                            placeholder="Выбери подменную кассету"
                                        />
                                    </label>
                                )}

                                {actionState.mode === 'guarantee' && replacementState.loaded && !replacementState.items.length ? (
                                    <div className="shell-inlineMessage shell-inlineMessage--error">
                                        Подменных кассет сейчас нет.
                                    </div>
                                ) : null}
                                {replacementState.error ? (
                                    <div className="shell-inlineMessage shell-inlineMessage--error">
                                        {replacementState.error}
                                    </div>
                                ) : null}

                                <div className="shell-searchActions">
                                    <button
                                        type="submit"
                                        className="shell-actionButton shell-actionButton--primary"
                                        disabled={actionState.loading}
                                    >
                                        {actionState.loading ? 'Сохраняем...' : 'Применить'}
                                    </button>
                                    <button
                                        type="button"
                                        className="shell-actionButton shell-actionButton--secondary"
                                        onClick={() => setActionState(initialActionState)}
                                    >
                                        Скрыть
                                    </button>
                                </div>
                            </form>

                            {actionState.error ? <div className="shell-inlineMessage shell-inlineMessage--error">{actionState.error}</div> : null}
                            {actionState.success ? <div className="shell-inlineMessage">{actionState.success}</div> : null}
                            {actionState.resultModule ? (
                                <div className="shell-requestDetail shell-requestDetail--compact">
                                    <div className="shell-requestDetailGrid">
                                        <div className="shell-requestDetailItem">
                                            <span>Обработанная кассета</span>
                                            <strong>{actionState.resultModule.serial_number}</strong>
                                        </div>
                                        <div className="shell-requestDetailItem">
                                            <span>Новый статус</span>
                                            <strong>{actionState.resultModule.repair_status || '—'}</strong>
                                        </div>
                                        {actionState.replacementModule ? (
                                            <div className="shell-requestDetailItem">
                                                <span>Подменная кассета</span>
                                                <strong>{actionState.replacementModule.serial_number}</strong>
                                            </div>
                                        ) : null}
                                    </div>
                                </div>
                            ) : null}
                        </section>
                    ) : null}
                </>
            ) : null}
        </div>
    );
}
